import { REACH_CLIENT_HEADER_VALUE } from './constants.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

/** Path + query starting with `/`. Strips accidental absolute URLs so callers never produce `API_HOST/http://ui/...`. */
export function normalizeApiEndpoint(endpoint: string): string {
  const trimmed = endpoint.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const parsed = new URL(trimmed);
      const pathAndQuery = `${parsed.pathname}${parsed.search}`;
      return pathAndQuery.startsWith('/') ? pathAndQuery : `/${pathAndQuery}`;
    } catch {
      // fall through
    }
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

/**
 * Generic utility for making authenticated API requests
 * @param endpoint - Relative path (e.g. '/partner/voice/call-analysis-tags')
 */
export async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const normalizedEndpoint = normalizeApiEndpoint(endpoint);
  const url = `${API_BASE_URL}${normalizedEndpoint}`;

  const response = await fetch(url, {
    ...options,
    credentials: 'include', // Include cookies for WorkOS session
    headers: {
      'Content-Type': 'application/json',
      'X-Reach-Client': REACH_CLIENT_HEADER_VALUE,
      ...options.headers,
    },
  });

  // Handle 401 Unauthorized - redirect to login since backend handles refresh automatically
  if (response.status === 401) {
    // Don't redirect if this is already an auth endpoint
    const isAuthEndpoint =
      url.includes('/api/auth/refresh') ||
      url.includes('/api/auth/session') ||
      url.includes('/api/auth/login') ||
      url.includes('/api/auth/logout');

    if (!isAuthEndpoint) {
      // Clear any existing auth state and redirect to login
      window.location.href = `${API_BASE_URL}/api/auth/logout`;
      throw new Error('Session expired. Please log in again.');
    }
  }

  if (!response.ok) {
    // Try to parse error response data
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = null;
    }

    // Extract error message from response
    const errorMessage =
      errorData?.message || errorData?.error || response.statusText || 'API request failed';

    throw new Error(errorMessage);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const responseText = await response.text();
  if (!responseText) {
    return undefined as T;
  }

  return JSON.parse(responseText) as T;
}
